/** Regulatory / clinical trial identifier types (Administrative Info form). */
export const REGULATORY_IDENTIFIER_TYPES = {
  eu_ct: {
    key: 'eu_ct',
    label: 'EU CT Number',
    fieldKey: 'eu_ct_number',
    displayPrefix: 'EU CT Number',
  },
  fda_ind: {
    key: 'fda_ind',
    label: 'FDA IND Number',
    fieldKey: 'fda_ind_number',
    displayPrefix: 'FDA IND Number',
  },
  ide: {
    key: 'ide',
    label: 'IDE Number',
    fieldKey: 'ide_number',
    displayPrefix: 'IDE Number',
  },
  jrct: {
    key: 'jrct',
    label: 'JRCT Number',
    fieldKey: 'jrct_number',
    displayPrefix: 'jRCT Number',
  },
  nct: {
    key: 'nct',
    label: 'NCT Number',
    fieldKey: 'nct_number',
    displayPrefix: 'NCT Number',
  },
  nmpa_ind: {
    key: 'nmpa_ind',
    label: 'NMPA IND Number',
    fieldKey: 'nmpa_ind_number',
    displayPrefix: 'NMPA IND Number',
  },
  who_utn: {
    key: 'who_utn',
    label: 'WHO/UTN Number',
    fieldKey: 'who_utn_number',
    displayPrefix: 'WHO/UTN Number',
    placeholder: 'UTN/WHO: Uxxxx-xxxx-xxxx',
  },
  other: {
    key: 'other',
    label: 'Other Regulatory or Clinical Trial Identifier',
    fieldKey: 'other_regulatory_identifier',
    displayPrefix: 'Other Identifier',
    allowMultiple: true,
  },
};

export const REGULATORY_IDENTIFIER_TYPE_LIST = Object.values(REGULATORY_IDENTIFIER_TYPES);

const trim = (v) => (v == null ? '' : String(v).trim());

export const getRegulatoryIdentifierDef = (typeKey) => REGULATORY_IDENTIFIER_TYPES[typeKey];

export const createEmptyRegulatoryEntry = (type, value = '') => ({
  id: `reg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  type,
  value,
});

/** Build UI rows from saved data (entries array or legacy flat fields). */
export const regulatoryEntriesFromInitialData = (data = {}) => {
  if (!data) return [];

  const rows = [];
  let idCounter = 0;
  const push = (type, value) => {
    const v = trim(value);
    if (!v) return;
    rows.push({
      id: `reg-${idCounter += 1}`,
      type,
      value: v,
    });
  };

  if (Array.isArray(data.regulatory_identifier_entries) && data.regulatory_identifier_entries.length > 0) {
    data.regulatory_identifier_entries.forEach((entry) => {
      const type = entry?.type;
      const def = REGULATORY_IDENTIFIER_TYPES[type];
      if (!def) return;
      push(type, entry.value);
    });
    return rows;
  }

  REGULATORY_IDENTIFIER_TYPE_LIST.forEach((def) => {
    const legacy = trim(data[def.fieldKey]);
    if (legacy) push(def.key, legacy);
  });

  return rows;
};

/** Serialize UI rows to API payload (entries + legacy flat fields). */
export const serializeRegulatoryEntries = (entries = []) => {
  const payload = { regulatory_identifier_entries: [] };

  REGULATORY_IDENTIFIER_TYPE_LIST.forEach((def) => {
    payload[def.fieldKey] = '';
  });

  const byType = {};
  entries.forEach((row) => {
    const def = REGULATORY_IDENTIFIER_TYPES[row.type];
    if (!def) return;
    const value = trim(row.value);
    if (!value) return;
    if (!byType[row.type]) byType[row.type] = [];
    byType[row.type].push(value);
    payload.regulatory_identifier_entries.push({ type: row.type, value });
  });

  REGULATORY_IDENTIFIER_TYPE_LIST.forEach((def) => {
    const values = byType[def.key] || [];
    if (def.allowMultiple) {
      payload[def.fieldKey] = values.join('; ');
    } else {
      payload[def.fieldKey] = values[0] || '';
    }
  });

  return payload;
};

export const countRegulatoryEntriesByType = (entries, type) =>
  entries.filter((e) => e.type === type).length;

/** Types still available in the dropdown (hide single-use types already added). */
export const availableRegulatoryTypesForDropdown = (entries = []) =>
  REGULATORY_IDENTIFIER_TYPE_LIST.filter((def) => {
    if (def.allowMultiple) return true;
    return countRegulatoryEntriesByType(entries, def.key) === 0;
  });

/** Title page / export block from entries or legacy admin document. */
export const formatRegulatoryIdentifiersForDisplay = (entries, adminInfo = {}) => {
  const lines = [];
  const seen = new Set();

  const addLine = (prefix, value) => {
    const v = trim(value);
    if (!v) return;
    lines.push(`${prefix}: ${v}`);
  };

  if (Array.isArray(entries) && entries.length > 0) {
    entries.forEach((entry) => {
      const def = REGULATORY_IDENTIFIER_TYPES[entry.type];
      if (!def) return;
      addLine(def.displayPrefix, entry.value);
    });
    return lines.join('  \n');
  }

  REGULATORY_IDENTIFIER_TYPE_LIST.forEach((def) => {
    if (seen.has(def.fieldKey)) return;
    const v = trim(adminInfo[def.fieldKey]);
    if (v) {
      seen.add(def.fieldKey);
      if (def.allowMultiple && v.includes(';')) {
        v.split(';')
          .map((s) => trim(s))
          .filter(Boolean)
          .forEach((part) => addLine(def.displayPrefix, part));
      } else {
        addLine(def.displayPrefix, v);
      }
    }
  });

  return lines.join('  \n');
};
